import React, { useEffect, useState } from "react";
import {
  X,
  Loader2,
  Settings2,
  RotateCw,
  Wand2,
  Bot,
  Star,
  TrendingUp,
  Megaphone,
  Share2,
  Check,
} from "lucide-react";
import { getAddonInfo } from "@/lib/creditAddons";
import { AddonInfoButton } from "@/components/AddonInfoButton";
import {
  LISTING_DEFAULT_ADDON_ONE_TIME,
  LISTING_DEFAULT_ADDON_WEEKLY,
  sanitizeListingAddonKeys,
} from "@/lib/listingDefaultAddons";
import { toast } from "sonner";
import { isServiceAvailable, serviceUnavailableMessage } from "@/lib/serviceAvailability";

const ONE_TIME_ICONS = {
  viewer_360: RotateCw,
  ai_photo_bg: Wand2,
  ai_customer_support: Bot,
  facebook_crosspost: Share2,
};

const WEEKLY_ICONS = {
  list_boost: Star,
  priority_ranking: TrendingUp,
  meta_ads: Megaphone,
};

/** Eladói alapértelmezett kiegészítők — minden új hirdetésre (kikapcsolható autónként). */
export function ListingDefaultsDialog({ open, onClose, user, onSave, pricingConfig }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const oneTimeCfg = pricingConfig?.addons_one_time || {};
  const weeklyCfg = pricingConfig?.addons_weekly || {};

  useEffect(() => {
    if (!open) return;
    setSelected(sanitizeListingAddonKeys(user?.default_listing_addons));
  }, [open, user?.default_listing_addons]);

  const toggle = (key) => {
    if (!selected.includes(key) && pricingConfig && !isServiceAvailable(pricingConfig, key)) {
      toast.error(serviceUnavailableMessage(pricingConfig, key));
      return;
    }
    setSelected((prev) => {
      const has = prev.includes(key);
      return has ? prev.filter((f) => f !== key) : [...prev, key];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(sanitizeListingAddonKeys(selected));
      toast.success("Hirdetés beállítások mentve");
      onClose();
    } catch (_) {
      toast.error("Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const n = selected.length;

  const renderCard = (key, { weekly = false } = {}) => {
    const info = getAddonInfo(key);
    const Icon = (weekly ? WEEKLY_ICONS : ONE_TIME_ICONS)[key] || Settings2;
    const active = selected.includes(key);
    const unavail = !active && pricingConfig && !isServiceAvailable(pricingConfig, key);
    const unavailMsg = unavail ? serviceUnavailableMessage(pricingConfig, key) : "";
    const cost = weekly ? Number(weeklyCfg[key]) || 0 : Number(oneTimeCfg[key]) || 0;
    return (
      <div
        key={key}
        role="button"
        tabIndex={unavail ? -1 : 0}
        data-testid={`listing-default-addon-${key}`}
        onClick={() => toggle(key)}
        onKeyDown={(e) => {
          if (unavail) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle(key);
          }
        }}
        className={`text-left rounded-2xl p-4 border transition-all w-full outline-none focus-visible:ring-2 focus-visible:ring-electric/50 ${
          active
            ? "border-electric bg-electric/10 cursor-pointer"
            : unavail
              ? "border-amber-500/25 bg-amber-500/5 opacity-80 cursor-not-allowed"
              : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer"
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
              active ? "bg-electric text-white" : "bg-white/[0.05] text-electric"
            }`}
          >
            {active ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" strokeWidth={1.75} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white flex items-center gap-1.5 flex-wrap">
              {info?.label || key}
              <AddonInfoButton addonKey={key} testid={`listing-default-addon-info-${key}`} />
              {cost > 0 && (
                <span className="text-electric text-xs font-mono">
                  {weekly ? `${cost} kr / hét` : `${cost} kr`}
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">{info?.shortDesc}</div>
            {unavail && (
              <div className="text-[10px] text-amber-300/90 mt-1">{unavailMsg}</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/70 backdrop-blur-sm"
      data-testid="listing-defaults-dialog"
    >
      <div className="w-full sm:max-w-2xl max-h-[92vh] flex flex-col rounded-t-3xl sm:rounded-2xl border border-white/10 bg-anthracite-900 shadow-2xl">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-white/10">
          <div>
            <div className="flex items-center gap-2 text-electric mb-1">
              <Settings2 className="w-4 h-4" strokeWidth={1.75} />
              <span className="text-[10px] uppercase tracking-[0.2em]">Eladói beállítás</span>
            </div>
            <h2 className="font-heading text-xl font-medium text-white">Hirdetés beállítások</h2>
            <p className="text-sm text-slate-400 mt-1 max-w-md">
              Itt kiválasztott <b className="text-slate-300">kiegészítők</b> minden új autódnál alapból bekapcsolódnak
              (360°, kiemelés, AI stb.). Egyedi hirdetésnél bármelyiket kikapcsolhatod — a kredit csak akkor vonódik le,
              amit ténylegesen aktiválsz.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-slate-400 hover:text-white shrink-0"
            aria-label="Bezárás"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {n > 0 && (
            <p className="text-xs text-electric" data-testid="listing-defaults-count">
              {n} alapértelmezett kiegészítő
            </p>
          )}

          <section>
            <h3 className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Egyszeri kiegészítők</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {LISTING_DEFAULT_ADDON_ONE_TIME.map((key) => renderCard(key))}
            </div>
          </section>

          <section>
            <h3 className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Heti kiegészítők</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {LISTING_DEFAULT_ADDON_WEEKLY.map((key) => renderCard(key, { weekly: true }))}
            </div>
          </section>
        </div>

        <div className="p-5 border-t border-white/10 flex flex-wrap gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-full text-sm text-slate-300 hover:bg-white/5"
          >
            Mégse
          </button>
          <button
            type="button"
            data-testid="listing-defaults-save"
            disabled={saving}
            onClick={save}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white text-sm font-medium"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Mentés
          </button>
        </div>
      </div>
    </div>
  );
}
